import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { push } from 'react-router-redux';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Media from 'react-media';
import styled from 'styled-components';
import theme from '@ow/ow-components/lib/theme';
import Button from '@ow/ow-components/lib/components/Button';
import Container from '@ow/ow-components/lib/components/Container';
import NoSSR from '@ow/ow-components/lib/components/NoSSR';
import lodashSortBy from 'lodash.sortby';
import lodashGroupBy from 'lodash.groupby';
import lodashGet from 'lodash.get';
import { getBrandSeoPathFromLocation } from '../../utils/RouteUtils';
import { getFilterValueFromSearchState } from '../../utils/searchUtils';
import { setRefineFilterString, setSearchStateForIndex } from '../../store/search/searchActions';
import { SEARCH_CONTEXT_BRAND_INFO } from '../../../constants/searchConstants';

const CAT_PATHS_CATEGORY_FACET_ID = 'catPaths';

const BrandTilesContainer = styled.div`
  width: 100%;
  padding-top: ${theme.space.double};
  padding-bottom: 30px;
`;

const BrandTilesWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.space.half};

  @media (max-width: ${theme.screenSizes.sm.max}px) {
    flex-direction: column;
    margin-bottom: 0;
  }
`;

const BrandTileWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${theme.colors.background2};
  border: solid 1px ${theme.colors.background2};
  padding: ${theme.space.double} ${theme.space.standard};
  cursor: pointer;

  @media ${theme.mediaSize.mediumMin} {
    :not(:last-child) {
      margin-right: ${theme.space.half};
    }
  }

  @media ${theme.mediaSize.smallMax} {
    margin-right: 0;
    margin-bottom: ${theme.space.half};
  }

  :hover {
    border: solid 1px ${theme.colors.primary};
  }
`;

const BrandTileHorizontalWrapper = styled(BrandTileWrapper)`
  flex-basis: ${props => (props.length <= 1 ? '100%' : `calc(${100 / props.length}%)`)};
`;

const BrandTileVerticalWrapper = styled(BrandTileWrapper)`
  flex-basis: calc(${props => 100 / props.length}%);
  flex-direction: column;
`;

const BrandTileInfoHorizontalWrapper = styled.div`
  flex-basis: calc(50% - 10px);
  text-align: center;
`;

const BrandTileInfoVerticalWrapper = styled.div`
  width: 100%;
  text-align: center;
`;

const BrandTileName = styled.h2`
  text-align: center;
  color: ${theme.colors.primary};
  font-size: 18px;
  margin-bottom: ${theme.space.standard};

  @media (max-width: ${theme.screenSizes.sm.max}px) {
    font-size: ${theme.fontSizes.lg};
  }
`;

const BrandTileNameVertical = styled(BrandTileName)`
  margin-top: ${theme.space.half};
`;

const BrandTileButton = styled(Button)`
  border-color: ${theme.colors.primary};
  :hover {
    background-color: ${theme.colors.primary};
    color: ${theme.colors.white};
  }
`;

const BrandTileImgHorizontalWrapper = styled.div`
  flex-basis: calc(50% - 10px);
`;

const BrandTileImgVerticalWrapper = styled.div`
  width: 100%;
  margin-bottom: 15px;
`;

const BrandTileImg = styled.img`
  max-width: 100%;
  max-height: 220px;
  margin: auto;
  display: block;
`;

class FeaturedBrandTiles extends Component {
  sortTiles = tiles => {
    return lodashGroupBy(lodashSortBy(tiles, ['row', 'column']), tile => tile.row);
  };

  clearFacetsAndRange = () => {
    const {
      setRefineFilterString,
      refinementList,
      setSearchStateForIndex,
      searchIndex
    } = this.props;
    setRefineFilterString(null);
    setSearchStateForIndex(
      {
        refinementList: refinementList[CAT_PATHS_CATEGORY_FACET_ID]
          ? { catPaths: refinementList[CAT_PATHS_CATEGORY_FACET_ID] }
          : {},
        range: {}
      },
      SEARCH_CONTEXT_BRAND_INFO,
      searchIndex
    );
  };

  renderVerticalTile = (index, length, tile) => {
    const { brandSeoPath, navigateToTileLink } = this.props;
    const ctaLabel = tile.buttonLabel || 'Shop now';
    const isSameBrand = tile.url.includes(brandSeoPath);

    return (
      <BrandTileVerticalWrapper
        key={`brand-tile-column-${index}`}
        data-ref={`brand-tile-column-vertical-${index}`}
        length={length}
        onClick={() => {
          this.clearFacetsAndRange();
          navigateToTileLink(isSameBrand, tile.url);
        }}
      >
        <BrandTileImgVerticalWrapper>
          <BrandTileImg data-ref="brand-tile-img" src={tile.image} />
        </BrandTileImgVerticalWrapper>
        <BrandTileInfoVerticalWrapper>
          <BrandTileNameVertical data-ref="brand-tile-name">{tile.name}</BrandTileNameVertical>
          <BrandTileButton data-ref="brand-tile-cta">{ctaLabel}</BrandTileButton>
        </BrandTileInfoVerticalWrapper>
      </BrandTileVerticalWrapper>
    );
  };

  renderHorizontalTile = (index, length, tile) => {
    const { brandSeoPath, navigateToTileLink } = this.props;
    const ctaLabel = tile.buttonLabel || 'Shop now';
    const isSameBrand = tile.url.includes(brandSeoPath);

    return (
      <BrandTileHorizontalWrapper
        key={`brand-tile-column-${index}`}
        data-ref={`brand-tile-column-horizontal-${index}`}
        length={length}
        onClick={() => {
          this.clearFacetsAndRange();
          navigateToTileLink(isSameBrand, tile.url);
        }}
      >
        <BrandTileInfoHorizontalWrapper>
          <BrandTileName data-ref="brand-tile-name">{tile.name}</BrandTileName>
          <BrandTileButton data-ref="brand-tile-cta">{ctaLabel}</BrandTileButton>
        </BrandTileInfoHorizontalWrapper>
        <BrandTileImgHorizontalWrapper>
          <BrandTileImg data-ref="brand-tile-img" src={tile.image} />
        </BrandTileImgHorizontalWrapper>
      </BrandTileHorizontalWrapper>
    );
  };

  render() {
    const { content } = this.props;
    if (!content || content.length === 0) {
      return null;
    }
    const sortedTiles = this.sortTiles(content);

    return (
      <BrandTilesContainer>
        <Container>
          <NoSSR>
            <Media query={`(min-width: ${theme.screenSizes.md.min}px)`}>
              {desktop =>
                Object.keys(sortedTiles).map((key, index) => (
                  <BrandTilesWrapper
                    key={`brand-tiles-row-${index}`}
                    data-ref={`brand-tiles-row-${index}`}
                  >
                    {sortedTiles[key].map((tile, index) => {
                      const tileLength = sortedTiles[key].length;
                      return desktop && tileLength < 3
                        ? this.renderHorizontalTile(index, tileLength, tile)
                        : this.renderVerticalTile(index, tileLength, tile);
                    })}
                  </BrandTilesWrapper>
                ))
              }
            </Media>
          </NoSSR>
        </Container>
      </BrandTilesContainer>
    );
  }
}

FeaturedBrandTiles.propTypes = {
  content: PropTypes.arrayOf(
    PropTypes.shape({
      image: PropTypes.string,
      name: PropTypes.string,
      url: PropTypes.string,
      buttonLabel: PropTypes.string,
      column: PropTypes.number,
      row: PropTypes.number
    })
  ),
  brandSeoPath: PropTypes.string,
  setRefineFilterString: PropTypes.func,
  setSearchStateForIndex: PropTypes.func,
  refinementList: PropTypes.object,
  searchIndex: PropTypes.string,
  navigateToTileLink: PropTypes.func
};

FeaturedBrandTiles.defaultProps = {
  content: [],
  refinementList: {},
  setRefineFilterString: () => {},
  setSearchStateForIndex: () => {},
  navigateToTileLink: () => {}
};

const mapStateToProps = ({ brandInfo, search, searchConfig }) => {
  const brandSeoPathFromLocation =
    typeof window !== 'undefined'
      ? lodashGet(
          getBrandSeoPathFromLocation(lodashGet(window, 'location.pathname')),
          'seoPath',
          ''
        )
      : '';
  const { searchIndex } = searchConfig;
  return {
    brandSeoPath: lodashGet(brandInfo, `brand[${brandSeoPathFromLocation}].seoPath`, ''),
    refinementList: getFilterValueFromSearchState(
      SEARCH_CONTEXT_BRAND_INFO,
      searchIndex,
      search,
      'refinementList'
    ),
    searchIndex
  };
};

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      setRefineFilterString,
      setSearchStateForIndex,
      navigateToTileLink: (isSameBrand, link) =>
        isSameBrand ? push(link) : window.location.assign(link)
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(FeaturedBrandTiles);
export { FeaturedBrandTiles };
